import {
    api,
    formatBps,
    formatMoney,
    ListChoiceFilter,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    DateTime,
    EditIcon,
    Field,
    FormSheet,
    Input,
    type ListColumnDef,
    Money,
    MoneyInput,
    PageHeader,
    PercentInput,
    RowActions,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { ReservePolicy } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';
import { CardScope } from '@/pages/pricing/cards';

const PATH = '/staff/reserve-policies';
const PAGE_KEY = 'reserve-policies';

export const reservePoliciesPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const policyFilters = filterDefs(PATH, {
    q: 'Search',
    include_closed: 'Versions',
    range: 'Created',
});

/** "10.00% for 180 days", plus ", up to $100,000.00" when the policy has a threshold (BM-150) */
export const reserveText = (p: {
    pct_bps?: number;
    hold_days?: number;
    threshold_cents?: number | null;
}) =>
    `${formatBps(p.pct_bps ?? 0)} for ${p.hold_days ?? 0} days` +
    (p.threshold_cents != null
        ? `, up to ${formatMoney(p.threshold_cents)}`
        : '');

function PolicyDialog({
    policy,
    onClose,
}: {
    /** null: a new tenant default policy */
    policy: ReservePolicy | null;
    onClose: () => void;
}) {
    const [name, setName] = useState(policy?.name ?? '');
    const [pct, setPct] = useState<number | null>(policy?.pct_bps ?? 1000);
    const [days, setDays] = useState(String(policy?.hold_days ?? 180));
    const [threshold, setThreshold] = useState<number | null>(
        policy?.threshold_cents ?? null,
    );
    const [merchant, setMerchant] = useState('');
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(async () => {
        const hold_days = Number.parseInt(days, 10);
        if (policy)
            return (
                await api().patch<ReservePolicy>(`${PATH}/${policy.id}`, {
                    name,
                    pct_bps: pct ?? 0,
                    hold_days,
                    threshold_cents: threshold,
                    version: policy.version,
                })
            ).data;
        return (
            await api().post<ReservePolicy>(PATH, {
                name,
                pct_bps: pct ?? 0,
                hold_days,
                threshold_cents: threshold,
                merchant_id: merchant.trim() || null,
            })
        ).data;
    }, [PATH]);
    const dirty =
        name !== (policy?.name ?? '') ||
        pct !== (policy?.pct_bps ?? 1000) ||
        days !== String(policy?.hold_days ?? 180) ||
        threshold !== (policy?.threshold_cents ?? null) ||
        merchant !== '';
    return (
        // BM-84 (rule E.9, design 0.7.0 reserve-policies): create and edit in a sheet
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title={policy ? `Edit ${policy.name}` : 'New reserve policy'}
            submitLabel={policy ? 'Save changes' : 'Create policy'}
            busy={write.isPending}
            submitDisabled={name.trim() === ''}
            dirty={dirty}
            error={error}
            data-testid="reserve-policy-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            <Field id="policy-name" label="Name">
                <Input
                    id="policy-name"
                    maxLength={80}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                    id="policy-pct"
                    label="Held from each sale"
                    hint="Of the gross including tax"
                >
                    <PercentInput id="policy-pct" bps={pct} onChange={setPct} />
                </Field>
                <Field
                    id="policy-days"
                    label="Released after"
                    hint="Days after capture, 0 to 730"
                >
                    <Input
                        id="policy-days"
                        inputMode="numeric"
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                    />
                </Field>
            </div>
            <Field
                id="policy-threshold"
                label="Stop holding at"
                hint="Empty: no threshold. New sales hold nothing once the merchant's reserve reaches this amount."
            >
                <MoneyInput
                    id="policy-threshold"
                    cents={threshold}
                    onChange={setThreshold}
                />
            </Field>
            {policy ? null : (
                <Field
                    id="policy-merchant"
                    label="Merchant (optional)"
                    hint="Empty: the tenant default"
                >
                    <Input
                        id="policy-merchant"
                        placeholder="mer_…"
                        className="font-mono"
                        value={merchant}
                        onChange={(e) => setMerchant(e.target.value)}
                    />
                </Field>
            )}
        </FormSheet>
    );
}

/**
 * BM-62 2.6: the rolling reserve policies. A sale holds the policy's percentage of its gross and releases it the
 * policy's days after capture; the merchant's assignment names the policy in force. BM-150: an optional threshold
 * caps the rolling pool (full percentage while under it, nothing at or over it).
 */
export function ReservePoliciesPage() {
    const can = useAuthStore((s) => s.can);
    const [editing, setEditing] = useState<ReservePolicy | null | 'new'>(null);
    const columns: ListColumnDef<ReservePolicy>[] = [
        {
            key: 'name',
            label: 'Policy',
            sortKey: 'name',
            boardCard: true,
            locked: 'start',
            csv: (p) => p.name,
            cell: (p) => <span className="font-medium">{p.name}</span>,
        },
        {
            key: 'merchant_id',
            aliases: ['scope'],
            label: 'Applies to',
            sortKey: 'merchant_id',
            csv: (p) => p.merchant_id ?? 'tenant default',
            cell: (p) => <CardScope merchantId={p.merchant_id} />,
        },
        {
            key: 'pct_bps',
            aliases: ['reserve'],
            label: 'Reserve',
            sortKey: 'pct_bps',
            boardCard: true,
            csv: (p) => p.pct_bps,
            cell: (p) => formatBps(p.pct_bps),
            noTruncate: true,
        },
        {
            key: 'hold_days',
            label: 'Held for',
            sortKey: 'hold_days',
            csv: (p) => p.hold_days,
            cell: (p) => `${p.hold_days} days`,
            noTruncate: true,
        },
        {
            key: 'threshold_cents',
            label: 'Threshold',
            sortKey: 'threshold_cents',
            csv: (p) => p.threshold_cents ?? '',
            cell: (p) =>
                p.threshold_cents != null ? (
                    <Money cents={p.threshold_cents} />
                ) : (
                    '—'
                ),
            noTruncate: true,
        },
        {
            key: 'active_from',
            label: 'Active from',
            kind: 'datetime',
            sortKey: 'active_from',
            csv: (p) => p.active_from,
            cell: (p) => <DateTime value={p.active_from} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'active_to',
            label: 'Closed',
            kind: 'datetime',
            sortKey: 'active_to',
            csv: (p) => p.active_to ?? '',
            cell: (p) =>
                p.active_to ? <DateTime value={p.active_to} empty="—" /> : '—',
            noTruncate: true,
        },
        {
            key: 'actions',
            label: 'Actions',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 reserve-policies): Edit in the row menu
            cell: (p) => (
                <RowActions
                    name={p.name}
                    data-testid={`edit-policy-${p.id}`}
                    items={[
                        {
                            label: 'Edit',
                            icon: EditIcon,
                            hidden: !can(P.pricingManage) || !!p.active_to,
                            onSelect: () => setEditing(p),
                        },
                    ]}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<ReservePolicy>(PAGE_KEY, PATH, {
        columns,
        filters: policyFilters,
    });
    return (
        <div>
            <PageHeader
                title="Reserve policies"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'A share of each sale held in the merchant reserve and released after the hold period. The sale keeps the policy it was captured under. With a threshold, new sales hold nothing while the reserve held is at or over it, and hold again once releases bring it under.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    can(P.pricingManage) ? (
                        <Button onClick={() => setEditing('new')}>
                            New policy
                        </Button>
                    ) : undefined
                }
            />
            <AdminListPage
                noun="policies"
                state={state}
                query={query}
                rowKey={(p) => p.id}
                controls={{
                    q: { kind: 'search' },
                    include_closed: (
                        <ListChoiceFilter
                            state={state}
                            name="include_closed"
                            label="Versions"
                            options={[
                                { value: 'true', label: 'Closed versions too' },
                            ]}
                        />
                    ),
                }}
            />
            {editing !== null ? (
                <PolicyDialog
                    policy={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                />
            ) : null}
        </div>
    );
}
